'use client';

/**
 * Text-only list row. No image, no card chrome — title + optional
 * duration/enrollment in a tight stack with hairline dividers and a
 * 4-to-12px padding-left hover shift (editorial affordance, not card-y).
 *
 * <p>Grid spec is {@code 'list'} — callers render a {@code <ul>}/stack
 * rather than a CSS grid.
 */
import { useTranslation } from 'react-i18next';
import { PortalLink } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { CourseProgressBar } from '../_shared/CourseProgressBar';
import { cardReset } from '../_shared/styles';
import type { CourseCardProps } from './CourseCard.types';
import type { CardGridSpec } from './gridSpecs';
import type { ProgressTextPlace } from './registry';

const muted = { color: 'var(--color-text-secondary, #5f6675)' };

export function MinimalCourseCard({
  course: c,
  progressBar: {
    show: showProgress = false,
    showText: showProgressText = false,
    textPlace: progressTextPlace = 'outside-right',
  } = {},
}: {
  course: CourseCardProps;
  progressBar?: { show?: boolean; showText?: boolean; textPlace?: ProgressTextPlace };
}) {
  const { t } = useTranslation('common');
  return (
    <Card asChild>
      <PortalLink
        href={c.href}
        style={{
          ...cardReset,
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 16,
          padding: 'var(--space-3-5, 14px) var(--space-1, 4px)',
          color: 'var(--color-text-primary, #0f1218)',
          textDecoration: 'none',
          position: 'relative',
          borderBottom: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
          transition: 'padding-left 180ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.paddingLeft = '12px';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.paddingLeft = '4px';
        }}
      >
        <span
          style={{
            fontSize: 'clamp(14px, 1.6vw, 15px)',
            fontWeight: 500,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {c.title}
        </span>
        {(c.duration || typeof c.enrolled === 'number') && (
          <span
            style={{
              ...muted,
              fontSize: 'var(--font-size-xs, 12px)',
              flexShrink: 0,
              display: 'flex',
              gap: 12,
            }}
          >
            {c.duration && <span>{c.duration}</span>}
            {typeof c.enrolled === 'number' && (
              <span>
                {c.enrolled.toLocaleString()} {t('label.participant')}
              </span>
            )}
          </span>
        )}
        {showProgress && typeof c.progress === 'number' && c.progress > 0 && (
          <div style={{ width: showProgressText ? 96 : 56, flexShrink: 0 }}>
            <CourseProgressBar
              progress={c.progress}
              showText={showProgressText}
              textPlace={progressTextPlace}
            />
          </div>
        )}
      </PortalLink>
    </Card>
  );
}

MinimalCourseCard.gridSpec = {
  kind: 'list',
} satisfies CardGridSpec;
